import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  MessagesSquare,
  ShoppingBag,
  Package,
  Coins,
  Menu,
  LogOut,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import { useLogout } from '../../api/auth';
import { ErrorBoundary } from '../ErrorBoundary';
import { Logo } from '../shared/Logo';
import { FullSpinner } from '../ui/Spinner';
import { prefetchRoute } from '../../lib/routePrefetch';
import { initials, cn } from '../../lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: 'Overview',
    items: [{ to: '/superadmin', label: 'Overview', icon: LayoutDashboard, end: true }],
  },
  {
    label: 'Accounts',
    items: [
      { to: '/superadmin/tenants', label: 'Tenants', icon: Building2 },
      { to: '/superadmin/users', label: 'Users', icon: Users },
    ],
  },
  {
    label: 'Activity',
    items: [
      { to: '/superadmin/conversations', label: 'Conversations', icon: MessagesSquare },
      { to: '/superadmin/orders', label: 'Orders', icon: ShoppingBag },
      { to: '/superadmin/products', label: 'Products', icon: Package },
    ],
  },
  {
    label: 'Platform',
    items: [{ to: '/superadmin/currencies', label: 'Currencies', icon: Coins }],
  },
];

const titles: Record<string, string> = {
  '/superadmin': 'Platform Overview',
  '/superadmin/tenants': 'Tenants',
  '/superadmin/users': 'Users',
  '/superadmin/conversations': 'Conversations',
  '/superadmin/orders': 'Orders',
  '/superadmin/products': 'Products',
  '/superadmin/currencies': 'Currencies',
};

function titleFor(pathname: string): string {
  if (titles[pathname]) return titles[pathname];
  if (pathname.startsWith('/superadmin/tenants/')) return 'Tenant Detail';
  return 'Super Admin';
}

function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <aside className="flex h-full w-64 flex-col border-r border-border bg-primary text-white">
      <div className="flex h-20 shrink-0 flex-col justify-center gap-1 border-b border-white/10 px-5">
        <Logo dark />
        <p className="pl-[42px] text-[10px] font-medium uppercase tracking-wide text-white/70">Super Admin</p>
      </div>

      <nav aria-label="Platform administration" className="flex-1 overflow-y-auto p-2.5 scrollbar-thin">
        {navSections.map((section, i) => (
          <div key={section.label} className={cn(i > 0 && 'mt-4')}>
            <p className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/70">
              {section.label}
            </p>
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={onNavigate}
                    onMouseEnter={() => prefetchRoute(item.to)}
                    onFocus={() => prefetchRoute(item.to)}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center gap-2.5 rounded-[8px] px-2.5 py-1.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-primary',
                        isActive
                          ? 'bg-white/10 text-white'
                          : 'text-white/80 hover:bg-white/5 hover:text-white',
                      )
                    }
                  >
                    <Icon className="h-[17px] w-[17px] shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-white/10 p-2.5">
        <NavLink
          to="/"
          onClick={onNavigate}
          className="flex items-center gap-2.5 rounded-[8px] px-2.5 py-1.5 text-[13px] font-medium text-white/80 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <LayoutDashboard className="h-[17px] w-[17px] shrink-0" />
          <span>Merchant app</span>
        </NavLink>
      </div>
    </aside>
  );
}

function SuperTopBar({ title }: { title: string }) {
  const user = useAuthStore((s) => s.user);
  const setMobileOpen = useUIStore((s) => s.setMobileSidebarOpen);
  const logout = useLogout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-white/80 px-4 backdrop-blur-md md:px-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setMobileOpen(true)}
          className="rounded-md p-2 text-mid transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 md:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold text-primary">{title}</h1>
      </div>

      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Account menu"
          className="flex items-center gap-2 rounded-md p-1 pr-2 transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
            {initials(user?.email ?? 'SA')}
          </span>
          <span className="hidden text-left sm:block">
            <span className="block text-sm font-medium leading-tight text-primary">
              {user?.email ?? 'Super Admin'}
            </span>
            <span className="block text-xs leading-tight text-mid">Platform staff</span>
          </span>
          <ChevronDown className="h-4 w-4 text-mid" />
        </button>

        {open && (
          <div className="absolute right-0 mt-2 w-56 overflow-hidden rounded-md border border-border bg-white py-1 shadow-float animate-pop-in">
            <div className="border-b border-border px-3 py-2.5">
              <p className="truncate text-sm font-medium text-primary">{user?.email}</p>
              <p className="text-xs text-mid">Super admin</p>
            </div>
            <button
              onClick={() => {
                setOpen(false);
                logout.mutate(undefined, { onSettled: () => navigate('/login') });
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-error transition-colors hover:bg-error/5"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export function SuperAdminShell() {
  const location = useLocation();
  const mobileOpen = useUIStore((s) => s.mobileSidebarOpen);
  const setMobileOpen = useUIStore((s) => s.setMobileSidebarOpen);
  const title = useMemo(() => titleFor(location.pathname), [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <div className="hidden md:block">
        <SidebarBody />
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-primary/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full animate-slide-in-left">
            <SidebarBody onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <SuperTopBar title={title} />
        <main className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-8">
            <ErrorBoundary key={location.pathname}>
              {/* Inner Suspense keeps the sidebar + top bar mounted while a
                  lazily-loaded page chunk is fetched. */}
              <Suspense fallback={<FullSpinner />}>
                <div key={location.pathname} className="animate-fade-in-up">
                  <Outlet />
                </div>
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
